import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  UserCheck,
  Smartphone,
  Users,
  KeyRound,
  RotateCcw,
  ShieldCheck,
  AlertTriangle,
  Clock,
  SignalHigh,
  Globe2,
  Route as RouteIcon,
  Filter,
  BadgeCheck,
  Eye,
  Gauge,
  Layers,
  Network,
  Headphones,
  Activity,
  Lock,
  CheckCircle,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";

const workflowSteps = [
  {
    title: "1. The user starts a flow",
    description: "Registration, login, account recovery or a verification step inside your application begins.",
  },
  {
    title: "2. Your application generates a code",
    description: "The verification logic lives in your own systems. Your platform creates the code and decides how long it is valid.",
  },
  {
    title: "3. The code is sent by SMS",
    description: "The message is carried to the user's phone number over the messaging provider's routes to the destination network.",
  },
  {
    title: "4. The user enters the code",
    description: "The user reads the message and types the code back into your registration, login or verification screen.",
  },
  {
    title: "5. Your application validates it",
    description: "Your systems compare the entered code with the one issued and confirm it is still within its validity window.",
  },
  {
    title: "6. The user proceeds",
    description: "If validation succeeds, the flow continues: the account is created, the login completes, or the action is confirmed.",
  },
];

const useCases = [
  {
    title: "New Account Registration",
    description:
      "Verify the phone number a user gives during signup, so every account is tied to a number its owner can actually receive messages on.",
    Icon: UserCheck,
    color: "text-indigo",
  },
  {
    title: "Phone Number Verification",
    description:
      "Confirm control of a number before it is attached to a profile, used as a contact point or set as a recovery channel.",
    Icon: Smartphone,
    color: "text-sky",
  },
  {
    title: "Customer Onboarding",
    description:
      "Add verification steps at the points in onboarding where you need more confidence about who is completing the process.",
    Icon: Users,
    color: "text-lime",
  },
  {
    title: "Login Verification",
    description:
      "Support sign-in flows that ask for a code in addition to, or instead of, a password, delivered to the registered number.",
    Icon: KeyRound,
    color: "text-amber",
  },
  {
    title: "Account Recovery",
    description:
      "Deliver codes during password reset and recovery flows so access returns to the person who controls the registered number.",
    Icon: RotateCcw,
    color: "text-violet",
  },
  {
    title: "Step-Up Authentication",
    description:
      "Ask for an additional confirmation when a session or action crosses a risk threshold, without blocking the user outright.",
    Icon: ShieldCheck,
    color: "text-pink",
  },
  {
    title: "High-Risk Account Actions",
    description:
      "Verify changes to credentials, payout details, limits or linked accounts with a code sent to the number on file.",
    Icon: AlertTriangle,
    color: "text-rose",
  },
];

const challenges = [
  {
    title: "Delayed Verification Codes",
    description:
      "A code that arrives a minute late often arrives after the user has already requested another one, which doubles traffic and doubles confusion.",
    Icon: Clock,
    color: "text-amber",
  },
  {
    title: "Failed Delivery",
    description:
      "Messages that never reach the handset leave users stuck at the verification screen, retrying or abandoning the flow entirely.",
    Icon: SignalHigh,
    color: "text-rose",
  },
  {
    title: "Expired Codes",
    description:
      "Short validity windows are good practice, but they make latency expensive: a slow route can turn a valid code into an expired one.",
    Icon: Gauge,
    color: "text-violet",
  },
  {
    title: "Inconsistent International Delivery",
    description:
      "A route that performs well in one market can behave differently in another, so verification quality often varies by destination.",
    Icon: Globe2,
    color: "text-sky",
  },
  {
    title: "Sender ID Issues",
    description:
      "Where sender identifiers must be registered or are overwritten, the verification message can look unfamiliar and be ignored.",
    Icon: BadgeCheck,
    color: "text-indigo",
  },
  {
    title: "Filtering",
    description:
      "Operator and regulatory filtering can hold or block traffic based on content, sender and sending patterns, sometimes without clear feedback.",
    Icon: Filter,
    color: "text-lime",
  },
  {
    title: "Repeated Code Requests",
    description:
      "Users who do not receive the first code request a second and a third, multiplying cost and load without improving the experience.",
    Icon: RotateCcw,
    color: "text-navy",
  },
  {
    title: "Lack of Delivery Visibility",
    description:
      "Without meaningful reporting, teams cannot tell whether the problem sits in their platform, the provider or the destination network.",
    Icon: Eye,
    color: "text-pink",
  },
];

const howWeSupport = [
  {
    title: "Telecom-Native Expertise",
    description:
      "Yootelco comes from the telecommunications side of messaging, with more than 17 years of experience in voice and messaging. That background shapes how verification traffic is routed and investigated.",
    Icon: Network,
    color: "text-indigo",
  },
  {
    title: "Quality Routing",
    description:
      "Verification traffic is routed through connections chosen for reliability rather than lowest cost, with routing decisions made per destination and traffic type.",
    Icon: RouteIcon,
    color: "text-amber",
  },
  {
    title: "Global Connectivity",
    description:
      "Messages are delivered through operator and carrier connectivity across the markets our clients serve, supporting consistent reach as user bases expand.",
    Icon: Globe2,
    color: "text-sky",
  },
  {
    title: "Monitoring and Operational Attention",
    description:
      "Traffic is monitored so performance changes are noticed and acted on, and so your team has delivery insight to work from when investigating an issue.",
    Icon: Activity,
    color: "text-lime",
  },
  {
    title: "Responsive Client Support",
    description:
      "A team built for enterprise clients works directly with your engineering, product and operations people instead of routing you through a ticket queue.",
    Icon: Headphones,
    color: "text-violet",
  },
  {
    title: "Practical Troubleshooting",
    description:
      "When a route looks wrong we investigate it at operator level and change it, rather than leaving traffic in place and reporting the same numbers back.",
    Icon: Gauge,
    color: "text-rose",
  },
];

const deliveryFactors = [
  {
    title: "Delivery Rate",
    description:
      "An undelivered marketing message is a lost impression. An undelivered verification code is a user who cannot finish signing up or signing in.",
    Icon: SignalHigh,
    color: "text-rose",
  },
  {
    title: "Latency",
    description:
      "Verification codes are read under time pressure, so consistent arrival within seconds matters as much as the overall delivery figure.",
    Icon: Clock,
    color: "text-amber",
  },
  {
    title: "Route Quality",
    description:
      "Direct, well-understood routes behave predictably. Long intermediary chains introduce variability that is difficult to diagnose after the fact.",
    Icon: RouteIcon,
    color: "text-indigo",
  },
  {
    title: "Sender ID",
    description:
      "A recognisable, correctly registered sender helps the user trust a verification message, and helps it reach the handset where registration rules apply.",
    Icon: BadgeCheck,
    color: "text-violet",
  },
  {
    title: "Filtering",
    description:
      "Operator filtering can hold or drop traffic based on content and sending patterns, which is why message structure and route choice are handled together.",
    Icon: Filter,
    color: "text-sky",
  },
  {
    title: "Redundancy",
    description:
      "Alternative routing paths matter when a network or connection degrades, so verification traffic does not depend on a single option.",
    Icon: Layers,
    color: "text-lime",
  },
  {
    title: "Monitoring",
    description:
      "Delivery visibility turns a vague report about missing codes into a specific destination, operator and time window your team can act on.",
    Icon: Activity,
    color: "text-pink",
  },
  {
    title: "Delivery Visibility",
    description:
      "A message accepted by a platform is not the same as a message read by a user. Reporting has to reflect what happened at the handset, not just at submission.",
    Icon: Eye,
    color: "text-navy",
  },
];

const faqs = [
  {
    question: "What is SMS identity verification?",
    answer:
      "SMS identity verification is the use of text messages to confirm that a user controls the phone number attached to an account or verification flow. In practice, a business sends a one-time verification code to the number a user provides, and the user enters that code to complete registration, onboarding, login or account recovery. It is one step within a broader identity verification process rather than the whole process: SMS confirms control of a number, while document checks, database lookups and other signals build the fuller picture of who the user is.",
  },
  {
    question: "How does SMS verification work?",
    answer:
      "The user starts a registration, login, recovery or verification flow. The business's own application generates a time-limited code and sends it to the user's phone number as an SMS through a messaging provider. The user reads the message and types the code back into the application, which validates it and lets the flow continue if it matches and is still valid. The messaging provider supplies the delivery layer, while the business's systems remain responsible for generating the code, checking it and deciding what happens next.",
  },
  {
    question: "What is an SMS verification code?",
    answer:
      "An SMS verification code is a short, single-use string of digits or characters sent by text message to confirm that the person completing an action can receive messages at a given phone number. It is generated by the business's application, is usually valid for a short window, and expires after use or after that window closes. Verification codes are used during account registration, phone number confirmation, login, recovery and step-up checks for higher-risk actions. They are sometimes called OTPs, verification PINs or confirmation codes.",
  },
  {
    question: "Why are identity verification SMS messages delayed?",
    answer:
      "Common causes include indirect routing to the destination network, operator or regulatory filtering, sender ID rules that are not met in a particular country, congestion on shared routes, and handsets that are temporarily unreachable, ported or switched off. Delays also build up when traffic passes through several intermediaries, each adding processing time. Because verification codes are time-limited, even a modest delay can leave a user requesting a second code, which is why route quality, monitoring and destination-level troubleshooting matter for this traffic.",
  },
  {
    question: "Is SMS enough for complete identity verification?",
    answer:
      "No. Receiving and returning a code sent by SMS demonstrates that the user controls a phone number, not that they are a specific person in the real world. Complete identity verification typically combines several elements: document verification, database or registry checks, device signals, biometrics and behavioural analysis, chosen according to the risk and regulatory needs of the business. SMS is a practical, widely reachable part of that workflow. It works best treated as one layer in a process, not as proof of identity on its own.",
  },
  {
    question: "What is the difference between identity verification and OTP authentication?",
    answer:
      "OTP authentication uses a one-time passcode, often delivered by SMS, to verify that a user controls a registered phone number or device at the moment of login or an action. Identity verification is a broader process that may include document checks, database lookups, biometrics and additional signals to establish who a user is. An SMS OTP is one possible step inside that process: it confirms control of a number, while identity verification aims to establish the identity behind it. The two are related but not interchangeable.",
  },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "SMS for Identity Verification",
  serviceType: "A2P SMS messaging",
  description:
    "Transactional SMS for identity verification and authentication workflows, including phone number verification, account registration, customer onboarding, login verification, account recovery and step-up authentication.",
  provider: {
    "@type": "Organization",
    name: "Yootelco",
    url: "https://yootelco.com",
  },
  areaServed: "Worldwide",
  audience: {
    "@type": "Audience",
    audienceType:
      "Fintech companies, banks, payment providers, ecommerce businesses, digital platforms and enterprises",
  },
  url: "https://yootelco.com/use-cases/identity-verification",
};

const UseCaseIdentityVerification = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="industries-page min-h-screen bg-white">
      <Seo
        title="SMS Identity Verification | Reliable Verification Messaging | Yootelco"
        description="Use reliable SMS for identity verification, phone verification and authentication workflows. Improve verification message delivery with Yootelco."
        path="/use-cases/identity-verification"
        faqs={faqs}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              Identity Verification Use Case
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              SMS for Identity Verification
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Businesses use SMS to verify phone numbers, confirm users during onboarding and support authentication workflows from login to account recovery. Yootelco delivers that verification traffic with a telecom-native approach: quality routing, timely delivery, global connectivity and operational visibility when a destination needs attention.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
              {[
                { label: "Verification SMS", Icon: UserCheck },
                { label: "Authentication Workflows", Icon: Lock },
                { label: "Transactional A2P SMS", Icon: SignalHigh },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
                  <item.Icon className="h-4 w-4 text-primary" strokeWidth={2.2} />
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">SMS for Identity Verification</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Identity verification shows up at several points in a customer lifecycle: confirming a phone number at registration, checking a user during onboarding, verifying a login, controlling account recovery and adding step-up authentication before sensitive actions. In each case, SMS carries the verification code or confirmation message that lets the flow continue.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            It is worth being precise about the role. SMS is a communication and verification channel: it confirms that the person completing an action can receive messages at a given number. It does not, by itself, establish someone's complete real-world identity. Businesses that need stronger assurance combine phone verification with document checks, database lookups, device signals and other controls inside their own verification stack.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            What SMS offers is reach and immediacy. It does not depend on an app being installed, push notifications being enabled or a data connection being available. For fintech companies, banks, payment providers, ecommerce businesses and digital platforms, that is why verification messaging is treated as{" "}
            <Link to="/solutions/a2p-sms" className="font-semibold text-primary underline-offset-4 hover:underline">
              A2P SMS
            </Link>{" "}
            infrastructure rather than a marketing channel.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">How SMS Identity Verification Works</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The workflow is deliberately simple, and the same pattern appears in registration, login, recovery and step-up flows across most platforms.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {workflowSteps.map((item) => (
            <article
              key={item.title}
              className="flex flex-col rounded-[18px] border border-border/60 bg-card px-6 py-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <h3 className="text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The responsibilities are split clearly. Your application generates the code, sets its validity window and validates what the user enters. Yootelco provides the messaging layer in between: getting the code from your platform to the user's phone, quickly and reliably, wherever that user happens to be.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Identity Verification SMS Use Cases</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The specific flows differ between a payment platform, a marketplace, a digital bank and an online service, but the verification points overlap heavily. These are the ones product and security teams bring to us most often.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {useCases.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Challenges */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Common Identity Verification SMS Challenges</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Most problems with verification messaging are not caused by the message itself. They come from how traffic is routed, how each destination network treats it, and how much visibility the sending team has when conditions change. In verification flows the effects are immediate: a delayed or missing code creates friction exactly where a user is waiting.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {challenges.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            No provider removes all of these issues, since operator behaviour and local regulation sit outside any single supplier's control. What route quality, monitoring and troubleshooting can improve is how often codes arrive on time, and how quickly a problem in one market is identified and corrected.
          </p>
        </div>
      </section>

      {/* How Yootelco Supports */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">How Yootelco Supports Identity Verification Messaging</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Yootelco does not verify identities, and we do not claim to. What we provide is the messaging layer your verification workflows depend on, built around route quality, monitoring and hands-on support rather than volume for its own sake.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {howWeSupport.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Teams that send verification traffic usually send authentication and security traffic too, so the same approach covers{" "}
            <Link to="/use-cases/otp-verification" className="font-semibold text-primary underline-offset-4 hover:underline">
              OTP verification
            </Link>{" "}
            and{" "}
            <Link to="/use-cases/fraud-prevention" className="font-semibold text-primary underline-offset-4 hover:underline">
              fraud and security alerts
            </Link>{" "}
            alongside identity verification.
          </p>
        </div>
      </section>

      {/* Delivery Quality */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Why SMS Delivery Quality Matters for Verification</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            A message being sent is not the same as a user receiving it quickly. Traffic can be accepted by a platform, passed between intermediaries, held by a filter and delivered long after the code expired, or dropped without a meaningful error. That gap between submission and arrival is where delivery quality lives, and verification traffic feels it first because every code is time-sensitive.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {deliveryFactors.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            This is where a telecom-native approach makes a practical difference. Coming from the carrier side means route decisions, operator relationships and delivery investigation are handled as network problems rather than support tickets. You can read more about how we approach{" "}
            <Link to="/coverage-routes" className="font-semibold text-primary underline-offset-4 hover:underline">
              coverage and routing
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Global */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Global SMS for Identity Verification</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            User bases rarely sit in one country. Customers register from different markets, travel, and hold numbers from more than one operator. Each destination brings its own mobile networks, filtering practices, sender ID requirements and traffic conditions, so verification that performs well domestically does not automatically perform well elsewhere.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Route differences show up as varying latency and inconsistent delivery behaviour between regions. Direct carrier connectivity matters because it shortens the path between your platform and the user's handset. Fewer intermediaries means fewer places for a code to be delayed or filtered, and clearer answers when one destination starts behaving differently from the rest.
          </p>
          <div className="mt-[24px] mx-auto max-w-[640px] text-left">
            {[
              "Direct connections to mobile network operators",
              "Routing configured per destination and traffic type",
              "Delivery monitoring across international routes",
              "Support for sender ID requirements where local rules apply",
              "Route changes made when a destination underperforms",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <p className="text-[15px] leading-[1.55] text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Identity Verification vs OTP */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Identity Verification vs OTP Authentication</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            These two terms are often used interchangeably, and the distinction matters. An OTP, usually delivered by SMS, is a method for confirming that a user controls a phone number or device at a specific moment: during login, a payment or another sensitive action. It answers a narrow question well.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Identity verification is broader. It can involve document checks, database lookups, biometrics, device intelligence and behavioural signals, assembled to build confidence about who a user actually is. SMS can be one part of that workflow: the phone verification step, the recovery channel, or the step-up check. But receiving an OTP should not be described as complete identity verification by itself.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            In practice most platforms use both together. The{" "}
            <Link to="/use-cases/otp-verification" className="font-semibold text-primary underline-offset-4 hover:underline">
              OTP verification
            </Link>{" "}
            flow handles the moment of authentication, while the wider identity process runs in the business's own systems, with SMS providing the messaging that connects the two.
          </p>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Need Reliable Delivery for Verification Messaging?"
        description="Tell us about your identity verification, onboarding and authentication messaging requirements and we'll show you how Yootelco can support them."
        useCase="Identity Verification SMS"
      />

      {/* FAQ */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Conclusion */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Verification Messaging That Arrives on Time</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Verification is judged at the moment of use. A code that arrives within seconds lets a user finish registering, signing in or recovering an account without thinking about the infrastructure behind it. A code that arrives late, or not at all, turns that moment into retries, support contacts and friction your team then has to explain.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Timely verification messaging comes down to delivery quality: latency, route choice, sender presentation, redundancy and the visibility to see when a destination changes behaviour. Yootelco's telecom-native approach, built on more than 17 years of voice and messaging experience, direct operator connectivity, monitoring and responsive support, is designed for exactly this kind of enterprise verification traffic. Talk to our team about your requirements, or use the{" "}
            <Link to="/contact" className="font-semibold text-primary underline-offset-4 hover:underline">
              contact form
            </Link>{" "}
            to share the destinations that matter most.
          </p>
          <Link
            to="/contact"
            className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
          >
            Contact Sales
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default UseCaseIdentityVerification;
